import { TableCell } from '@material-ui/core'
import { FormattedDate, FormattedMessage } from 'react-intl'
import LinkToItemShipmentTracking, {
  getCarrierId,
} from '../OrderDetail/LinkToItemShipmentTracking'
import OrderDetailItemsTrackingChina from '../OrderDetail/OrderDetailItemsTrackingChina'
import { useGetShipmentTrackingInfoQuery } from '@src/queries/GetShipmentTrackingInfoQuery.generated'
import {
  MergedOrderItemScheduleLine,
  OrderItemFragmentWithMergedLines,
} from './utils'
import { CarrierId, OrderItemTrackingInfo } from '@src/types/graphql-types'
import { OrderDetailsFragment } from '@src/queries/OrderDetailsQuery.generated'

interface ShipmentTrackingCellProps {
  line: MergedOrderItemScheduleLine
  itemGroup: OrderItemFragmentWithMergedLines
  orderDetails?: OrderDetailsFragment
  isChinaUser: boolean
  setTrackingInfo: React.Dispatch<
    React.SetStateAction<OrderItemTrackingInfo | null>
  >
}

const EstimatedDeliveryTrackingInfo: React.FC<{
  tracking: OrderItemTrackingInfo
}> = ({ tracking }) => {
  const carrierId = getCarrierId(tracking.trackingUrl)
  const { data } = useGetShipmentTrackingInfoQuery({
    variables: {
      trackingNumber: tracking.trackingNumber,
      carrierId: carrierId || CarrierId.Fedex,
    },
    skip: !carrierId || !tracking.trackingNumber,
    ssr: false,
  })

  if (data?.getShipmentTrackingInfo?.deliveryDate) {
    return <div>-</div>
  }

  return (
    <div>
      {tracking.estimatedDeliveryDate ? (
        <FormattedDate
          value={new Date(tracking.estimatedDeliveryDate)}
          year="numeric"
          month="short"
          day="numeric"
          timeZone="UTC"
        />
      ) : (
        '-'
      )}
    </div>
  )
}

export const ShipmentTrackingCell: React.FC<ShipmentTrackingCellProps> = ({
  line,
  itemGroup,
  orderDetails,
  isChinaUser,
  setTrackingInfo,
}) => {
  const hasEstimatedDelivery = line.trackingInfo.some(
    (tracking) => tracking?.estimatedDeliveryDate
  )

  return (
    <>
      <TableCell>
        {hasEstimatedDelivery ? (
          <>
            <FormattedMessage
              id="ESTIMATED_DELIVERY_DATE"
              defaultMessage="Estimated Delivery Date"
            />
            :
            {line.trackingInfo.map((tracking: OrderItemTrackingInfo) => (
              <EstimatedDeliveryTrackingInfo
                key={tracking.trackingNumber}
                tracking={tracking}
              />
            ))}
          </>
        ) : (
          <>
            <FormattedMessage id="CARRIER" defaultMessage="Carrier" />:
            {(!Array.isArray(line.trackingInfo) ||
              line.trackingInfo.length === 0) && <div>-</div>}
            {line?.trackingInfo.length > 0 &&
              line.trackingInfo.map((tracking: OrderItemTrackingInfo) => (
                <div key={tracking.trackingNumber}>
                  {getCarrierId(tracking.trackingUrl)?.toUpperCase()}
                </div>
              ))}
          </>
        )}
      </TableCell>

      <TableCell>
        <FormattedMessage id="TRACKING" defaultMessage="Tracking" />:
        {isChinaUser ? (
          <OrderDetailItemsTrackingChina
            line={line}
            setTrackingInfo={setTrackingInfo}
            excludeLabel
            excludeDeliveryId
          />
        ) : line.trackingInfo.length > 0 ? (
          line.trackingInfo.map((tracking: OrderItemTrackingInfo) => (
            <div key={tracking.trackingNumber}>
              <LinkToItemShipmentTracking
                tracking={tracking}
                materialNumber={itemGroup.materialNumber}
                orderNumber={orderDetails?.orderNumber}
                shipToZip={orderDetails?.shipTo?.postalCode}
              />
            </div>
          ))
        ) : (
          <div>-</div>
        )}
      </TableCell>
    </>
  )
}
